import { h, Component } from 'preact';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';

import { SIDEBAR_ITEMS } from '../sidebar/sidebarDefaults';

const COLOR_OPTIONS = [
    ['primary', 'Primary'],
    ['secondary', 'Secondary'],
    ['success', 'Success'],
    ['danger', 'Danger'],
    ['warning', 'Warning'],
    ['info', 'Info'],
    ['light', 'Light'],
    ['dark', 'Dark'],
];

const STYLE_OPTIONS = [
    ['outline', 'Outline'],
    ['solid', 'Solid'],
];

function genUuid() {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID();
    }
    return `qb-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

@observer
class FrontpageQuickButtonsEditor extends Component {
    constructor(props) {
        super(props);
        this.state = {
            showPicker: false,
            showCustomForm: false,
            customDraft: { label: '', icon: '', href: '' },
            editingUuid: null,
            saving: false,
        };
        this.saveTimer = null;
    }

    componentWillUnmount() {
        if (this.saveTimer) clearTimeout(this.saveTimer);
    }

    getButtons() {
        const { customer } = this.props;
        return Array.isArray(customer?.frontpageQuickButtons)
            ? customer.frontpageQuickButtons.slice()
            : [];
    }

    scheduleSave(next) {
        const { customerStore, customer } = this.props;
        this.setState({ saving: true });
        if (this.saveTimer) clearTimeout(this.saveTimer);
        this.saveTimer = setTimeout(async () => {
            try {
                await customerStore.save({ id: customer.id, frontpageQuickButtons: next });
            } finally {
                this.setState({ saving: false });
            }
        }, 750);
    }

    commit(next) {
        const { customerStore, objectName = 'customer' } = this.props;
        customerStore.updateObjectKeyValue(objectName, 'frontpageQuickButtons', next);
        this.scheduleSave(next);
    }

    setField = (uuidv4, field, value) => {
        const next = this.getButtons().map(b => (b.uuidv4 === uuidv4
            ? { ...b, [field]: value }
            : b));
        this.commit(next);
    };

    moveUp = (idx) => {
        const buttons = this.getButtons();
        if (idx <= 0 || idx >= buttons.length) return;
        const next = buttons.slice();
        [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
        this.commit(next);
    };

    moveDown = (idx) => {
        const buttons = this.getButtons();
        if (idx < 0 || idx >= buttons.length - 1) return;
        const next = buttons.slice();
        [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
        this.commit(next);
    };

    remove = (uuidv4) => {
        const next = this.getButtons().filter(b => b.uuidv4 !== uuidv4);
        this.commit(next);
    };

    addSidebar = (sidebarKey) => {
        if (!sidebarKey) return;
        const next = this.getButtons().concat({ uuidv4: genUuid(), sidebarKey });
        this.commit(next);
        this.setState({ showPicker: false });
    };

    startCustom = () => {
        this.setState({
            showCustomForm: true,
            editingUuid: null,
            customDraft: { label: '', icon: '', href: '' },
        });
    };

    startEditCustom = (btn) => {
        this.setState({
            showCustomForm: true,
            editingUuid: btn.uuidv4,
            customDraft: { label: btn.label || '', icon: btn.icon || '', href: btn.href || '' },
        });
    };

    cancelCustom = () => {
        this.setState({ showCustomForm: false, editingUuid: null });
    };

    updateDraft = (field) => (e) => {
        const value = e.target.value;
        this.setState(state => ({ customDraft: { ...state.customDraft, [field]: value } }));
    };

    saveCustom = () => {
        const { customDraft, editingUuid } = this.state;
        const label = (customDraft.label || '').trim();
        const icon = (customDraft.icon || '').trim();
        const href = (customDraft.href || '').trim();
        if (!label || !icon || !href || !href.startsWith('/')) return;

        let next;
        if (editingUuid) {
            next = this.getButtons().map(b => (b.uuidv4 === editingUuid
                ? { ...b, label, icon, href, sidebarKey: undefined }
                : b));
        } else {
            next = this.getButtons().concat({ uuidv4: genUuid(), label, icon, href });
        }
        this.commit(next);
        this.setState({ showCustomForm: false, editingUuid: null });
    };

    renderRow(btn, idx, total) {
        const isSidebar = !!btn.sidebarKey;
        let label = btn.label || '';
        let icon = btn.icon || 'fa-solid fa-link';
        let target = btn.href || '';
        let translationKey = null;
        if (isSidebar) {
            const entry = SIDEBAR_ITEMS.find(e => e.key === btn.sidebarKey);
            if (entry) {
                label = entry.label;
                icon = entry.icon;
                target = entry.href;
                translationKey = `sidebar.${entry.key}`;
            } else {
                label = btn.sidebarKey;
                icon = 'fa-solid fa-triangle-exclamation';
            }
        }
        return (
            <div key={btn.uuidv4} class='d-flex align-items-center gap-2 py-1 border-bottom'>
                <button type='button' class='btn btn-sm btn-outline-secondary'
                    disabled={idx === 0}
                    onClick={() => this.moveUp(idx)}
                    title='Move up'>
                    <i class='fa-solid fa-arrow-up' />
                </button>
                <button type='button' class='btn btn-sm btn-outline-secondary'
                    disabled={idx === total - 1}
                    onClick={() => this.moveDown(idx)}
                    title='Move down'>
                    <i class='fa-solid fa-arrow-down' />
                </button>
                <i class={`${icon} text-body-secondary`} style='width: 1.5em; text-align: center;' />
                <select
                    class='form-select form-select-sm'
                    style='width: auto;'
                    value={btn.color || 'primary'}
                    onChange={(e) => this.setField(btn.uuidv4, 'color', e.target.value)}
                    title='Color'
                >
                    {COLOR_OPTIONS.map(([value, label]) => (
                        <option key={value} value={value}>{label}</option>
                    ))}
                </select>
                <select
                    class='form-select form-select-sm'
                    style='width: auto;'
                    value={btn.style || 'outline'}
                    onChange={(e) => this.setField(btn.uuidv4, 'style', e.target.value)}
                    title='Style'
                >
                    {STYLE_OPTIONS.map(([value, label]) => (
                        <option key={value} value={value}>{label}</option>
                    ))}
                </select>
                <div class='flex-fill d-flex flex-column'>
                    <span class='fw-semibold'>
                        {translationKey
                            ? <Text id={translationKey}>{label}</Text>
                            : label}
                    </span>
                    <small class='text-body-secondary'>
                        {isSidebar
                            ? <Text id='quickButtons.sidebarSource' fields={{ key: btn.sidebarKey, href: target }}>
                                Menu item: {btn.sidebarKey} ({target})
                              </Text>
                            : target}
                    </small>
                </div>
                {!isSidebar && (
                    <button type='button' class='btn btn-sm btn-outline-primary'
                        onClick={() => this.startEditCustom(btn)}
                        title='Edit'>
                        <i class='fa-solid fa-pen' />
                    </button>
                )}
                <button type='button' class='btn btn-sm btn-outline-danger'
                    onClick={() => this.remove(btn.uuidv4)}
                    title='Delete'>
                    <i class='fa-solid fa-trash' />
                </button>
            </div>
        );
    }

    renderPicker() {
        const buttons = this.getButtons();
        const usedKeys = new Set(buttons.map(b => b.sidebarKey).filter(Boolean));
        const available = SIDEBAR_ITEMS.filter(e => e.action === 'route' && !usedKeys.has(e.key));
        return (
            <div class='mt-2 p-2 border rounded bg-body-secondary'>
                <div class='d-flex justify-content-between align-items-center mb-2'>
                    <strong>
                        <Text id='quickButtons.pickFromMenu'>Pick from menu</Text>
                    </strong>
                    <button type='button' class='btn btn-sm btn-link'
                        onClick={() => this.setState({ showPicker: false })}>
                        <Text id='common.close'>Close</Text>
                    </button>
                </div>
                {available.length === 0
                    ? <em class='text-body-secondary'>
                        <Text id='quickButtons.allSidebarPicked'>All menu items already added.</Text>
                      </em>
                    : (
                        <div class='d-flex flex-wrap gap-1'>
                            {available.map(e => (
                                <button key={e.key} type='button'
                                    class='btn btn-sm btn-outline-primary'
                                    onClick={() => this.addSidebar(e.key)}>
                                    <i class={`${e.icon} me-1`} />
                                    <Text id={`sidebar.${e.key}`}>{e.label}</Text>
                                </button>
                            ))}
                        </div>
                    )}
            </div>
        );
    }

    renderCustomForm() {
        const { customDraft, editingUuid } = this.state;
        const labelOk = !!(customDraft.label || '').trim();
        const iconOk = !!(customDraft.icon || '').trim();
        const hrefOk = !!(customDraft.href || '').trim() && customDraft.href.startsWith('/');
        const canSave = labelOk && iconOk && hrefOk;
        return (
            <div class='mt-2 p-2 border rounded bg-body-secondary d-flex flex-column gap-2'>
                <strong>
                    {editingUuid
                        ? <Text id='quickButtons.editCustom'>Edit custom link</Text>
                        : <Text id='quickButtons.addCustom'>Add custom link</Text>}
                </strong>
                <input type='text' class='form-control form-control-sm'
                    placeholder='Label (e.g. Conventions)'
                    value={customDraft.label}
                    onInput={this.updateDraft('label')} />
                <input type='text' class='form-control form-control-sm'
                    placeholder='Icon class (e.g. fa-duotone fa-bullseye-pointer)'
                    value={customDraft.icon}
                    onInput={this.updateDraft('icon')} />
                <input type='text' class='form-control form-control-sm'
                    placeholder='Internal path (must start with /)'
                    value={customDraft.href}
                    onInput={this.updateDraft('href')} />
                {!hrefOk && (customDraft.href || '').length > 0 && (
                    <small class='text-danger'>
                        <Text id='quickButtons.hrefMustStart'>Path must start with /</Text>
                    </small>
                )}
                <div class='d-flex gap-2'>
                    <button type='button' class='btn btn-sm btn-primary'
                        disabled={!canSave}
                        onClick={this.saveCustom}>
                        <Text id='common.save'>Save</Text>
                    </button>
                    <button type='button' class='btn btn-sm btn-outline-secondary'
                        onClick={this.cancelCustom}>
                        <Text id='common.cancel'>Cancel</Text>
                    </button>
                </div>
            </div>
        );
    }

    render() {
        const { saving, showPicker, showCustomForm } = this.state;
        const buttons = this.getButtons();
        return (
            <div class='w-100 p-3'>
                <div class='d-flex justify-content-between align-items-center mb-2'>
                    <h6 class='m-0'>
                        <Text id='quickButtons.title'>Frontpage quick buttons</Text>
                    </h6>
                    {saving && (
                        <small class='text-body-secondary'>
                            <i class='fa-solid fa-spinner fa-spin me-1' />
                            <Text id='common.saving'>Saving…</Text>
                        </small>
                    )}
                </div>
                <small class='text-body-secondary d-block mb-2'>
                    <Text id='quickButtons.help'>
                        Buttons appear at the top of the frontpage. Pick existing menu items or add custom internal links. 5–10 recommended.
                    </Text>
                </small>

                {buttons.length === 0 && (
                    <div class='text-body-secondary mb-2'>
                        <em><Text id='quickButtons.empty'>No quick buttons configured.</Text></em>
                    </div>
                )}

                {buttons.map((b, idx) => this.renderRow(b, idx, buttons.length))}

                <div class='d-flex gap-2 mt-3'>
                    <button type='button' class='btn btn-sm btn-outline-primary'
                        onClick={() => this.setState({ showPicker: !showPicker, showCustomForm: false })}>
                        <i class='fa-solid fa-plus me-1' />
                        <Text id='quickButtons.pickFromMenu'>Pick from menu</Text>
                    </button>
                    <button type='button' class='btn btn-sm btn-outline-secondary'
                        onClick={() => { this.setState({ showPicker: false }); this.startCustom(); }}>
                        <i class='fa-solid fa-plus me-1' />
                        <Text id='quickButtons.addCustom'>Add custom link</Text>
                    </button>
                </div>

                {showPicker && this.renderPicker()}
                {showCustomForm && this.renderCustomForm()}
            </div>
        );
    }
}

export default FrontpageQuickButtonsEditor;
